/**
 * Helpers for reading `tri:*` BPMN extension attributes that the server
 * forwards on user-task worklist items and conversations.
 *
 * Plumbing (server → portal) is generic: every `tri:<key>` attribute on a
 * `<bpmn:userTask>` is exposed under `triAttrs.<key>` (prefix stripped).
 * New attrs flow through automatically — no per-attribute code changes.
 *
 * Sources, in priority order:
 *   1. `task.triAttrs[key]` — set by `enrichWorklistWithTriAttrs` on the
 *      server, or by `applyActiveTask` on the client.
 *   2. `task.extensions['tri:<key>']` — raw extensions from the SDK callback,
 *      surfaced on `HumanTask` projections in legacy code paths.
 *   3. `conversation.triAttrsByNodeId[task.nodeId][key]` — top-level map
 *      attached by the server so model-level property stripping doesn't lose
 *      the data.
 */

export type TriAttrs = Record<string, string>;

function getConversationTriAttrs(
  task: any,
  conversation?: any,
): TriAttrs | undefined {
  if (!task?.nodeId) return undefined;
  const fromConv = (conversation as any)?.triAttrsByNodeId?.[task.nodeId];
  if (!fromConv || Object.keys(fromConv).length === 0) return undefined;
  return fromConv;
}

function getTaskExtensionTriAttrs(task: any): any {
  const extensions = task?.extensions;
  if (!extensions || typeof extensions !== "object") return undefined;

  const triEntries = Object.entries(extensions).filter(
    (entry): entry is [string, string] =>
      entry[0].startsWith("tri:") && typeof entry[1] === "string"
  );
  if (triEntries.length === 0) return undefined;

  return Object.fromEntries(
    triEntries.map(([key, value]) => [key.slice(4), value]),
  );
}

export function getTaskTriAttrs(
  task: any,
  conversation?: any,
): TriAttrs | undefined {
  const fromExtensions = getTaskExtensionTriAttrs(task);
  const fromConv = getConversationTriAttrs(task, conversation);
  const fromTask =
    task?.triAttrs && Object.keys(task.triAttrs).length > 0
      ? task.triAttrs
      : undefined;

  // Per-key fallback with source priority:
  // task.triAttrs > task.extensions[tri:*] > conversation.triAttrsByNodeId[nodeId]
  const merged = {
    ...(fromConv ?? {}),
    ...(fromExtensions ?? {}),
    ...(fromTask ?? {}),
  };

  return Object.keys(merged).length > 0 ? merged : undefined;
}

export function getTriAttr(
  task: any,
  key: string,
  conversation?: any,
): string | undefined {
  return (
    task?.triAttrs?.[key] ??
    getTaskExtensionTriAttrs(task)?.[key] ??
    getConversationTriAttrs(task, conversation)?.[key]
  );
}
